import type { SortingState } from "@tanstack/react-table"
import { useMemo, useState } from "react"

import type { File, SignedUrl } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"

const useFileTableSorting = ({
  fileList,
  isActiveCompany,
  isArchivedVisible,
}: {
  fileList: SignedUrl[]
  isActiveCompany: boolean
  isArchivedVisible: boolean
}): {
  data: SignedUrl[]
  sorting: SortingState
  handleSortingChange: (updaterFn: (state: SortingState) => SortingState) => void
} => {
  const [sortingField, setSortingField] = useState<keyof File | null>("fieldGroup")
  const [sortingOrder, setSortingOrder] = useState<SortingOrder>(SortingOrder.Asc)

  const sorting = useMemo(() => {
    if (!sortingField) return []

    return [
      {
        id: sortingField,
        desc: sortingOrder === SortingOrder.Desc,
      },
    ]
  }, [sortingField, sortingOrder])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    const sort = newSorting[0]
    setSortingField(sort.id as keyof File) // Cast sort.id to keyof File
    setSortingOrder(sort.desc ? SortingOrder.Desc : SortingOrder.Asc)
  }

  const data = useMemo(() => {
    let processedfileList = fileList
    if (!isActiveCompany || !isArchivedVisible) {
      processedfileList = processedfileList.filter((i) => !i.file?.isArchived)
    }

    if (!sortingField) return processedfileList

    return [...processedfileList].sort((a, b) => {
      const aValue = a.file?.[sortingField]
      const bValue = b.file?.[sortingField]
      if (typeof aValue === "string" && typeof bValue === "string") {
        if (aValue < bValue) return sortingOrder === SortingOrder.Asc ? -1 : 1
        if (aValue > bValue) return sortingOrder === SortingOrder.Asc ? 1 : -1
      }

      return 0
    })
  }, [fileList, isActiveCompany, isArchivedVisible, sortingField, sortingOrder])

  return {
    data,
    sorting,
    handleSortingChange,
  }
}
export default useFileTableSorting
